import React, { useState, useEffect, useCallback } from "react";
import { Card, Badge, Avatar, Button } from "components/ui";
import {
  CalendarDaysIcon,
  CalendarIcon,
  ClockIcon,
  UserCircleIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  PlusIcon,
  ArrowPathIcon,
  FlagIcon,
  FireIcon,
  PaperAirplaneIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DevicePhoneMobileIcon,
  EllipsisVerticalIcon,
  UserIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { hasPermission } from "utils/permissionsUtils";
import { getCommunicationAndTelephonyStatus } from "../../../services/leadsService";
import {
  formatDate,
  formatTime,
  getLocalDateTime,
} from "../../../utils/dateUtils";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { Fragment } from "react";
import { useActivityTypes } from "hooks/useFollowUp";
import clsx from "clsx";

const formatTimeLocal = (timeStr) => {
  if (!timeStr) return "";
  if (/AM|PM/i.test(timeStr)) {
    return timeStr;
  }
  const match = timeStr.match(/^(\d{1,2}):(\d{2})/);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${minutes} ${ampm}`;
  }
  return timeStr;
};

// Final statuses that cannot be updated further
const FINAL_STATUS_IDS = [2, 3, 4, 5]; // Completed, Missed, Cancelled, Failed
const FINAL_STATUS_NAMES = ["Completed", "Missed", "Cancelled", "Failed"];

// Helper to check if a follow-up status is final
const isStatusFinal = (followUp) => {
  if (!followUp) return false;
  const statusId = followUp.StatusId || followUp.statusId;
  const statusName = followUp.StatusName || followUp.status;
  return (
    FINAL_STATUS_IDS.includes(statusId) ||
    FINAL_STATUS_NAMES.includes(statusName)
  );
};

// Helper to check if a follow-up is overdue
const isFollowUpOverdue = (followUp) => {
  if (!followUp || !followUp.ScheduledDate) return false;
  const scheduledDate = getLocalDateTime(followUp.ScheduledDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return scheduledDate < today;
};

const StatusCards = ({
  isMobile,
  leadData,
  onAssignOwner,
  onEditFollowUp,
  onUpdateStatus,
  onAddFollowUp,
  // Lead Management handlers
  onChangeStage,
  onSetPriority,
  onSetInteractionLevel,
  onAssignAcademic,
  onAssignAccountant,
  isReadOnly,
}) => {
  // State for communication and telephony stats
  const [communicationStats, setCommunicationStats] = useState({
    emailSent: 0,
    smsSent: 0,
  });
  const [telephonyStats, setTelephonyStats] = useState({
    inboundCalls: 0,
    outboundCalls: 0,
  });
  const [currentFollowUpIndex, setCurrentFollowUpIndex] = useState(0);
  const { types: activityTypes, isLoading: loadingActivityTypes } =
    useActivityTypes();

  const leadId = leadData?.id || leadData?.leadId || leadData?.LeadId;

  // Fetch communication and telephony stats
  const fetchStats = useCallback(async () => {
    if (!leadId) {
      return;
    }

    try {
      const response = await getCommunicationAndTelephonyStatus(leadId);
      if (response?.success && response.data) {
        const statusData = response.data;

        setCommunicationStats({
          emailSent: statusData.emailSent ?? statusData.EmailSent ?? 0,
          smsSent: statusData.smsSent ?? statusData.SMSSent ?? 0,
        });

        setTelephonyStats({
          inboundCalls:
            statusData.inboundCalls ??
            statusData.inboundCall ??
            statusData.InBoundCalls ??
            0,
          outboundCalls:
            statusData.outboundCalls ??
            statusData.outboundCall ??
            statusData.OutBoundCalls ??
            0,
        });
      }
    } catch (error) {
      console.error("Error fetching communication stats:", error);
    }
  }, [leadId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  // Helper function to get the next upcoming follow-up
  const getNextFollowUp = () => {
    if (
      !leadData ||
      !leadData.upcomingFollowUps ||
      leadData.upcomingFollowUps.length === 0
    ) {
      return null;
    }

    // Filter pending follow-ups and sort by date
    const pendingFollowUps = leadData.upcomingFollowUps
      .filter(
        (followUp) =>
          followUp.Status === 1 || followUp.StatusName === "Pending",
      )
      .sort(
        (a, b) =>
          getLocalDateTime(a.ScheduledDate) - getLocalDateTime(b.ScheduledDate),
      );

    return pendingFollowUps.length > 0 ? pendingFollowUps[0] : null;
  };

  // Helper function to get all pending follow-ups (for carousel)
  const getPendingFollowUps = () => {
    if (
      !leadData ||
      !leadData.upcomingFollowUps ||
      leadData.upcomingFollowUps.length === 0
    ) {
      return [];
    }

    return leadData.upcomingFollowUps
      .filter(
        (followUp) =>
          followUp.Status === 1 || followUp.StatusName === "Pending",
      )
      .sort(
        (a, b) =>
          getLocalDateTime(a.ScheduledDate) - getLocalDateTime(b.ScheduledDate),
      );
  };

  const allPendingFollowUps = getPendingFollowUps();
  const currentFollowUp = allPendingFollowUps[currentFollowUpIndex] || null;

  // Reset index when follow-ups change
  useEffect(() => {
    if (currentFollowUpIndex >= allPendingFollowUps.length) {
      setCurrentFollowUpIndex(Math.max(0, allPendingFollowUps.length - 1));
    }
  }, [allPendingFollowUps.length, currentFollowUpIndex]);

  // Navigation handlers
  const handlePrevFollowUp = () => {
    setCurrentFollowUpIndex((prev) =>
      prev > 0 ? prev - 1 : allPendingFollowUps.length - 1,
    );
  };

  const handleNextFollowUp = () => {
    setCurrentFollowUpIndex((prev) =>
      prev < allPendingFollowUps.length - 1 ? prev + 1 : 0,
    );
  };

  const isAssigned = (name) => {
    if (!name) return false;
    if (Array.isArray(name)) return name.length > 0;
    const n = name.toString().trim().toLowerCase();
    return n !== "n/a" && n !== "unassigned" && n !== "";
  };
  const owners = Array.isArray(leadData?.Owners)
    ? leadData.Owners
    : isAssigned(leadData?.Owners)
      ? [{ OwnerName: leadData.Owners, isPrimary: true }]
      : [];
  const isOwnerAssigned = owners.length > 0;

  const academicCounsellorName =
    leadData?.SourceDetails?.LeadAcademicCounsellorName ||
    leadData?.LeadAcademicCounsellorName;
  const isAcademicAssigned = isAssigned(academicCounsellorName);

  const accountsCounsellorName =
    leadData?.SourceDetails?.LeadAccountantCounsellorName ||
    leadData?.accountsCounsellorName ||
    leadData?.accountsCounsellor;
  const isAccountsAssigned = isAssigned(accountsCounsellorName);

  const hasAnyPersonnel =
    isOwnerAssigned || isAcademicAssigned || isAccountsAssigned;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Lead Management Section */}
      <div>
        <h3
          className={`${isMobile ? "text-sm" : "text-sm"} dark:text-dark-100 mb-4 flex items-center gap-2 font-bold text-gray-900`}
        >
          <div className="h-5 w-1 rounded-full bg-[#fe0046]"></div>
          Lead Management
        </h3>
        <div
          className={`grid ${isMobile ? "grid-cols-1 gap-2" : "grid-cols-3 gap-3"}`}
        >
          {hasPermission("MLLE") && onChangeStage && (
            <Button
              variant="outlined"
              color="primary"
              onClick={onChangeStage}
              disabled={leadData?.IsStudent}
              className={`justify-center !px-3 !py-2.5 text-[13px] ${leadData?.IsStudent ? "cursor-not-allowed opacity-60" : ""}`}
            >
              <ArrowPathIcon className="mr-2 h-3.5 w-3.5" />
              Change Stage
            </Button>
          )}
          {hasPermission("MLLE") && onSetPriority && (
            <Button
              variant="outlined"
              color="warning"
              onClick={onSetPriority}
              disabled={leadData?.IsStudent}
              className={`justify-center !px-3 !py-2.5 text-[13px] ${leadData?.IsStudent ? "cursor-not-allowed opacity-60" : ""}`}
            >
              <FlagIcon className="mr-2 h-3.5 w-3.5" />
              Set Priority
            </Button>
          )}
          {hasPermission("MLLE") && onSetInteractionLevel && (
            <Button
              variant="outlined"
              color="neutral"
              onClick={onSetInteractionLevel}
              disabled={leadData?.IsStudent}
              className={`justify-center !px-3 !py-2.5 text-[13px] ${leadData?.IsStudent ? "cursor-not-allowed opacity-60" : ""}`}
            >
              <FireIcon className="mr-2 h-3.5 w-3.5" />
              Set Interaction
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards Grid - 3 columns */}
      <div
        className={`grid ${isMobile ? "mb-4 grid-cols-1 gap-4" : "mb-0 grid-cols-1 gap-6 md:grid-cols-2"}`}
      >
        {/* Communication Stats Card */}
        <Card
          className={`${isMobile ? "p-4" : "p-5"} dark:border-dark-700/50 dark:bg-dark-800 group relative overflow-hidden border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:shadow-2xl`}
        >
          <div className="relative mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-pink-50 transition-colors group-hover:bg-pink-100 dark:bg-pink-900/40 dark:group-hover:bg-pink-900/60">
              <PaperAirplaneIcon className="h-5 w-5 text-pink-600 dark:text-pink-400" />
            </div>
            <div>
              <h3 className="dark:text-dark-100 text-[14px] font-bold text-gray-900">
                Communication Stats
              </h3>
              <p className="dark:text-dark-400 text-[11px] text-gray-500">
                Interaction overview
              </p>
            </div>
          </div>

          <div className="relative space-y-3.5">
            {[
              {
                label: "Email Sent",
                value: communicationStats.emailSent,
                icon: EnvelopeIcon,
                color: "text-blue-500",
                bg: "bg-blue-500/10",
              },
              {
                label: "SMS Sent",
                value: communicationStats.smsSent,
                icon: ChatBubbleLeftRightIcon,
                color: "text-teal-500",
                bg: "bg-teal-500/10",
              },
              {
                label: "Inbound Call",
                value: telephonyStats.inboundCalls,
                icon: PhoneIcon,
                color: "text-indigo-500",
                bg: "bg-indigo-500/10",
              },
              {
                label: "Outbound Call",
                value: telephonyStats.outboundCalls,
                icon: PhoneIcon,
                color: "text-purple-500",
                bg: "bg-purple-500/10",
              },
            ].map((stat, idx) => (
              <div
                key={idx}
                className="dark:hover:bg-dark-700/30 flex items-center justify-between rounded-lg p-2 text-[13px] transition-colors hover:bg-gray-50"
              >
                <div className="dark:text-dark-300 flex items-center gap-2.5 text-gray-600">
                  <div className={`rounded-md p-1.5 ${stat.bg}`}>
                    <stat.icon className={`h-3.5 w-3.5 ${stat.color}`} />
                  </div>
                  <span className="font-medium">{stat.label}</span>
                </div>
                <span className="dark:text-dark-50 dark:bg-dark-700 rounded bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-900">
                  {stat.value}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* Upcoming Follow-ups Card */}
        <Card
          className={`${isMobile ? "p-4" : "p-5"} dark:border-dark-700/50 dark:bg-dark-800 group flex min-h-[180px] flex-col overflow-hidden border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:shadow-2xl`}
        >
          <div className="relative mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 transition-colors group-hover:bg-blue-100 dark:bg-blue-900/40 dark:group-hover:bg-blue-900/60">
                <CalendarDaysIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="dark:text-dark-100 text-[14px] font-bold text-gray-900">
                  Activities
                </h3>
                <p className="dark:text-dark-400 text-[11px] text-gray-500">
                  Scheduled events
                </p>
              </div>
            </div>

            {currentFollowUp && hasPermission("FUPSEFUP") && (
              <Popover className="relative">
                <PopoverButton className="dark:hover:bg-dark-700 dark:text-dark-400 dark:hover:text-dark-200 rounded-lg p-1.5 text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-700">
                  <EllipsisVerticalIcon className="h-5 w-5" />
                </PopoverButton>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <PopoverPanel className="dark:bg-dark-800 dark:border-dark-700 absolute right-0 z-50 mt-1 w-44 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-2xl">
                    <div className="space-y-1 p-1.5">
                      {currentFollowUp &&
                        onEditFollowUp &&
                        !isStatusFinal(currentFollowUp) && (
                          <button
                            onClick={() => {
                              if (leadData?.IsStudent) return;
                              onEditFollowUp(currentFollowUp);
                            }}
                            disabled={leadData?.IsStudent}
                            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-[13px] font-bold text-blue-700 transition-all hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                            Edit Details
                          </button>
                        )}
                      {currentFollowUp &&
                        onUpdateStatus &&
                        !isStatusFinal(currentFollowUp) && (
                          <button
                            onClick={() => {
                              if (leadData?.IsStudent) return;
                              onUpdateStatus(currentFollowUp);
                            }}
                            disabled={leadData?.IsStudent}
                            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-[13px] font-bold text-green-700 transition-all hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/30"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            Update Status
                          </button>
                        )}
                    </div>
                  </PopoverPanel>
                </Transition>
              </Popover>
            )}
          </div>

          <div className="relative flex flex-1 flex-col items-center justify-center py-4">
            {currentFollowUp ? (
              <div className="w-full space-y-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <p className="border-primary-500 border-l-2 pl-2 text-[13px] font-bold text-gray-900">
                      {(() => {
                        const dateStr = currentFollowUp.ScheduledDate
                          ? currentFollowUp.ScheduledDate.split("T")[0]
                          : "";
                        let displayDate = "";
                        if (dateStr) {
                          const [year, month, day] = dateStr
                            .split("-")
                            .map(Number);
                          const localDate = new Date(year, month - 1, day);
                          const dVal = String(localDate.getDate()).padStart(
                            2,
                            "0",
                          );
                          const mVal = String(
                            localDate.getMonth() + 1,
                          ).padStart(2, "0");
                          const yVal = String(localDate.getFullYear()).slice(
                            -2,
                          );
                          displayDate = `${dVal}/${mVal}/${yVal}`;
                        }
                        const displayTime = currentFollowUp.ScheduledTime
                          ? formatTimeLocal(currentFollowUp.ScheduledTime)
                          : "";
                        return `${displayDate} at ${displayTime || "Not set"}`;
                      })()}
                    </p>
                    <p className="dark:text-dark-400 line-clamp-2 pl-2 text-xs text-gray-500 italic">
                      {currentFollowUp.Remarks ||
                        currentFollowUp.remarks ||
                        currentFollowUp.Notes ||
                        currentFollowUp.notes ||
                        currentFollowUp.FollowUpNote ||
                        currentFollowUp.NoteDescription ||
                        currentFollowUp.FollowUpRemarks ||
                        "No remarks"}
                    </p>
                    {(currentFollowUp.AssignedTo ||
                      currentFollowUp.assignedToName) && (
                      <p className="dark:text-dark-300 mt-1 flex items-center gap-1.5 pl-2 text-[11px] font-medium text-gray-600">
                        <UserIcon className="h-3 w-3 text-gray-400" />
                        Assigned to:{" "}
                        {currentFollowUp.AssignedTo ||
                          currentFollowUp.assignedToName}
                      </p>
                    )}
                  </div>
                  {isFollowUpOverdue(currentFollowUp) && (
                    <Badge
                      variant="soft"
                      color="error"
                      className="text-[10px] font-black uppercase"
                    >
                      Overdue
                    </Badge>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center">
                <span className="dark:text-dark-400 text-sm font-medium text-gray-400">
                  No pending follow-ups
                </span>
              </div>
            )}
          </div>

          {onAddFollowUp && hasPermission("FUPSCFUP") && (
            <Menu
              as="div"
              className="relative mt-auto inline-block w-full text-left"
            >
              <MenuButton as={Fragment}>
                <Button
                  disabled={leadData?.IsStudent}
                  variant="outlined"
                  color="primary"
                  className={`w-full justify-center rounded-lg !py-2 text-xs font-bold transition-all ${leadData?.IsStudent ? "cursor-not-allowed opacity-60" : ""}`}
                >
                  <PlusIcon className="mr-1.5 h-3.5 w-3.5 stroke-[3px]" />
                  Schedule Activity
                  <ChevronDownIcon className="ml-1 h-3.5 w-3.5 opacity-60" />
                </Button>
              </MenuButton>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <MenuItems className="dark:bg-dark-700 absolute right-0 bottom-full z-50 mb-2 max-h-60 w-48 origin-bottom-right overflow-y-auto rounded-xl border border-gray-200/60 bg-white shadow-[0_20px_50px_rgba(0,0,0,0.18)] ring-1 ring-black/10 focus:outline-none dark:border-gray-700/50 dark:shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
                  <div className="p-1">
                    {loadingActivityTypes ? (
                      <div className="flex items-center gap-2 px-4 py-3 text-xs text-gray-400">
                        <div className="border-primary-400 h-3 w-3 animate-spin rounded-full border-2 border-t-transparent" />
                        Loading...
                      </div>
                    ) : activityTypes.length === 0 ? (
                      <div className="px-4 py-3 text-xs text-gray-400">
                        No types available
                      </div>
                    ) : (
                      activityTypes.map((type) => (
                        <MenuItem key={type.id ?? type.channelId}>
                          {({ active }) => (
                            <button
                              onClick={() => onAddFollowUp(type)}
                              className={clsx(
                                "flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors",
                                active
                                  ? "bg-primary-50 text-primary-600 dark:bg-primary-900/20"
                                  : "dark:text-dark-200 text-gray-700",
                              )}
                            >
                              {type.label}
                            </button>
                          )}
                        </MenuItem>
                      ))
                    )}
                  </div>
                </MenuItems>
              </Transition>
            </Menu>
          )}
        </Card>

        {/* Assigned Personnel Card - Modern Profile Style */}
        <Card
          className={`${isMobile ? "p-4" : "p-5"} dark:border-dark-700/50 dark:bg-dark-800 group relative flex flex-col overflow-hidden border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:shadow-2xl`}
        >
          <div className="flex justify-between">
            <div className="relative mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50 transition-colors group-hover:bg-purple-100 dark:bg-purple-900/40 dark:group-hover:bg-purple-900/60">
                <UserCircleIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="dark:text-dark-100 text-[14px] font-bold text-gray-900">
                  Assigned Users
                </h3>
                <p className="dark:text-dark-400 text-[11px] text-gray-500">
                  Team ownership
                </p>
              </div>
            </div>{" "}
            {onAssignOwner && hasPermission("MLASGN") && (
              <button
                onClick={() => {
                  onAssignOwner();
                }}
                className="dark:hover:bg-dark-600 hover:text-primary-600 rounded-md p-1 text-gray-400 transition-all hover:bg-gray-100"
              >
                <PencilSquareIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="relative flex-1 space-y-3">
            {owners.map((owner, idx) => (
              <div
                key={idx}
                className="dark:border-dark-700 flex items-center justify-between border-b border-gray-50 py-2 last:border-0"
              >
                <div className="flex items-center gap-3">
                  <Avatar
                    name={owner.OwnerName}
                    size={7}
                    initialColor="primary"
                  />
                  <div>
                    <p className="dark:text-dark-50 text-[13px] leading-tight font-bold text-gray-900">
                      {owner.OwnerName}
                    </p>
                    <p className="text-[10px] font-medium text-gray-500">
                      {owner.isPrimary ? "Lead Owner (Primary)" : "Lead Owner"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default StatusCards;
